import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type SetWithClose = (content: React.ReactNode, onClose?: (() => void) | undefined) => void;

/** Setters only - stable reference. Use in screens that call setPanel/setEdit so they don't re-render when content changes (avoids lag during drag/resize). */
type SidebarSettersValue = {
  setMenu: SetWithClose;
  setEdit: SetWithClose;
  setPanel: SetWithClose;
};

/** Full content + setters. Use only in GlobalSidebarHost which needs to render the content. */
type SidebarContentContextValue = SidebarSettersValue & {
  menuContent: React.ReactNode;
  menuOnClose?: (() => void) | undefined;
  editContent: React.ReactNode;
  editOnClose?: (() => void) | undefined;
  panelContent: React.ReactNode;
  panelOnClose?: (() => void) | undefined;
};

const SidebarSettersContext = createContext<SidebarSettersValue | undefined>(undefined);
const SidebarContentContext = createContext<SidebarContentContextValue | undefined>(undefined);

/** Use in screens (itinerary, map, etc.) - only returns setters. Won't re-render when panel content updates. */
export function useSidebarSetters() {
  const ctx = useContext(SidebarSettersContext);
  if (!ctx) throw new Error('useSidebarSetters must be used within SidebarContentProvider');
  return ctx;
}

/** Use in GlobalSidebarHost - returns content + setters. Re-renders when content changes. */
export function useSidebarContent() {
  const ctx = useContext(SidebarContentContext);
  if (!ctx) throw new Error('useSidebarContent must be used within SidebarContentProvider');
  return ctx;
}

export function SidebarContentProvider({ children }: { children: React.ReactNode }) {
  const [menuState, setMenuState] = useState<{
    content: React.ReactNode;
    onClose?: (() => void) | undefined;
  }>({ content: null });
  const [editState, setEditState] = useState<{
    content: React.ReactNode;
    onClose?: (() => void) | undefined;
  }>({ content: null });
  const [panelState, setPanelState] = useState<{
    content: React.ReactNode;
    onClose?: (() => void) | undefined;
  }>({ content: null });

  const setMenu = useCallback((content: React.ReactNode, onClose?: (() => void) | undefined) => {
    setMenuState({ content, onClose });
  }, []);

  const setEdit = useCallback((content: React.ReactNode, onClose?: (() => void) | undefined) => {
    setEditState({ content, onClose });
  }, []);

  const setPanel = useCallback((content: React.ReactNode, onClose?: (() => void) | undefined) => {
    setPanelState({ content, onClose });
  }, []);

  const settersValue = useMemo(
    () => ({ setMenu, setEdit, setPanel }),
    [setMenu, setEdit, setPanel]
  );

  const contentValue = useMemo(
    () => ({
      ...settersValue,
      menuContent: menuState.content,
      menuOnClose: menuState.onClose,
      editContent: editState.content,
      editOnClose: editState.onClose,
      panelContent: panelState.content,
      panelOnClose: panelState.onClose,
    }),
    [
      settersValue,
      menuState.content,
      menuState.onClose,
      editState.content,
      editState.onClose,
      panelState.content,
      panelState.onClose,
    ]
  );

  return (
    <SidebarSettersContext.Provider value={settersValue}>
        <SidebarContentContext.Provider value={contentValue}>
          {children}
        </SidebarContentContext.Provider>
      </SidebarSettersContext.Provider>
  );
}
